import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface PlayerProfile {
  id: string;
  full_name: string | null;
  level: number | null;
}

interface ActiveMatchCourt {
  id: string;
  name: string;
}

interface ActiveMatchRound {
  id: string;
  round_number: number;
  status: string | null;
  tournament_id: string;
  tournament: {
    id: string;
    name: string;
    format: string;
    points_per_match: number;
    player_result_entry: boolean;
    result_confirmation: string;
    auto_accept_minutes: number | null;
    auto_advance_rounds: boolean;
  };
}

export interface ActiveMatch {
  id: string;
  round_id: string;
  court_id: string | null;
  team1_players: string[];
  team2_players: string[];
  score_team1: number | null;
  score_team2: number | null;
  status: string | null;
  reported_by: string | null;
  court: ActiveMatchCourt | null;
  round: ActiveMatchRound;
  profiles: Map<string, PlayerProfile>;
}

export function useActiveMatch() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["active-match", user?.id],
    queryFn: async (): Promise<ActiveMatch | null> => {
      // Find matches where user is on either team and status is pending or reported
      const { data: matches, error } = await supabase
        .from("tournament_match_results")
        .select(
          "*, courts(id, name), tournament_rounds(id, round_number, status, tournament_id, tournaments(id, name, format, points_per_match))"
        )
        .or("status.eq.pending,status.eq.reported");

      if (error) throw error;
      if (!matches || matches.length === 0) return null;

      // Filter client-side for matches containing the user
      const userId = user!.id;
      const userMatch = (matches as any[]).find(
        (m) =>
          m.team1_players?.includes(userId) ||
          m.team2_players?.includes(userId)
      );

      if (!userMatch) return null;

      // Gather all player IDs for profile lookup
      const allPlayerIds = [
        ...(userMatch.team1_players ?? []),
        ...(userMatch.team2_players ?? []),
      ];

      const { data: profileRows } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level")
        .in("id", allPlayerIds);

      const profiles = new Map<string, PlayerProfile>(
        (profileRows ?? []).map((p: any) => [p.id, p])
      );

      const round = userMatch.tournament_rounds as any;
      const tournament = round?.tournaments as any;

      return {
        id: userMatch.id,
        round_id: userMatch.round_id,
        court_id: userMatch.court_id,
        team1_players: userMatch.team1_players,
        team2_players: userMatch.team2_players,
        score_team1: userMatch.score_team1,
        score_team2: userMatch.score_team2,
        status: userMatch.status,
        reported_by: userMatch.reported_by,
        court: userMatch.courts
          ? { id: userMatch.courts.id, name: userMatch.courts.name }
          : null,
        round: {
          id: round.id,
          round_number: round.round_number,
          status: round.status,
          tournament_id: round.tournament_id,
          tournament: {
            id: tournament.id,
            name: tournament.name,
            format: tournament.format,
            points_per_match: tournament.points_per_match,
            // These fields may not exist in DB yet; default gracefully
            player_result_entry:
              (tournament as any).player_result_entry ?? true,
            result_confirmation:
              (tournament as any).result_confirmation ?? "opponent",
            auto_accept_minutes:
              (tournament as any).auto_accept_minutes ?? null,
            auto_advance_rounds:
              (tournament as any).auto_advance_rounds ?? false,
          },
        },
        profiles,
      };
    },
    enabled: !!user?.id,
    refetchInterval: 15_000,
  });
}
